import * as _ from 'lodash';
import { Fragment, ReactElement } from 'react';
import { DropdownButton, MenuItem, ButtonGroup, Glyphicon } from 'react-bootstrap';
import { PendingSwapsActionItem, SwapsToShow } from '../models';
import { IActionButtons } from './action-buttons';
import styled from 'styled-components';
import { FinancialAgreement } from '@nkr/views/match/documents/financial-agreement';


const ButtonGroupWrapper = styled(ButtonGroup)`
    @media (max-width: 1823px) {
        & > div.dropdown > button,
        & > div.dropdown > ul > li > a {
            font-size: 12px;
        }
    }
`;

enum ActionButtonGroup {
    Top = 0,
    Donors = 1,
    Recipients = 2,
    Serology = 3,
    FinalXm = 4,
    Swap = 5,
    Other = 99
}

interface IButton {
    show: boolean,
    key: number,
    text: string | ReactElement,
    disabled: boolean,
    group: ActionButtonGroup,
    displayOrder?: number
}

interface IProps {
    index: number,
    show: SwapsToShow,
    isInternal: boolean,
    data: IActionButtons,
    pullRight: boolean,
    matchHistoryId?: number,
    onItemSelected: (key: number) => void
}

const Actions = (props: IProps) => {
    const handleMenuItemSelect = (eventKey: any) => {
        props.onItemSelected(eventKey);
    };

    const {
        showPatientOutcomesButton,
        showAboFileButton,
        showDemographicsFileButton,
        showHlaFileButton, showRecipientHlaFileButton, showCtReportButton,
        showMedChartButton, showAboVerifButton,
        showViewSerologyButton, showAddSerologyButton, showReuploadSerologyButton,
        showUploadFinalXmButton, showViewFinalXmButton,
        showRequestFinalXm,
        hasExistingFinalXm,
        canRequestFinalXm,
        showEmergencyNotification, showRequestSerologyKit,
        canRequestSerology,
        showLogisticsPlan, showFormsAndChecklists,
        showDonorCtStudy,
        showCenterAboVerification, showDonorAboMolecular, swap, centerId
    } = props.data;

    const disabled = props.show == SwapsToShow.Past;

    const buttons: IButton[] = [
        { show: showLogisticsPlan ?? false, key: PendingSwapsActionItem.LogisticsPlan, text: 'Logistics Plan', disabled: false, group: ActionButtonGroup.Other },
        { show: showFormsAndChecklists ?? false, key: PendingSwapsActionItem.FormsAndChecklists, text: 'Forms & Checklists', disabled: false, group: ActionButtonGroup.Swap, displayOrder: 10 },
        // { show: showEmergencyNotification ?? false, key: PendingSwapsActionItem.EmergencyNotification, text: 'Emergency Notification', disabled, group: ActionButtonGroup.Top },
        { show: showRequestFinalXm ?? false, key: PendingSwapsActionItem.RequestFinalXM, text: <>Request {hasExistingFinalXm && <Glyphicon glyph='warning-sign' style={{ backgroundColor: 'yellow' }} title='There is an existing request for this item' />}</>, disabled, group: ActionButtonGroup.FinalXm },

        { show: showPatientOutcomesButton ?? false, key: PendingSwapsActionItem.PatientOutcomes, text: 'Patient Outcomes', disabled: false, group: ActionButtonGroup.Recipients },
        { show: showAboFileButton ?? false, key: PendingSwapsActionItem.RecipientAboFile, text: 'ABO File', disabled: false, group: ActionButtonGroup.Recipients },
        { show: showDemographicsFileButton ?? false, key: PendingSwapsActionItem.RecipientDemoFile, text: 'DEMO File', disabled: false, group: ActionButtonGroup.Recipients },
        { show: showRecipientHlaFileButton ?? false, key: PendingSwapsActionItem.RecipientHlaFile, text: 'HLA FILE', disabled: false, group: ActionButtonGroup.Recipients },

        { show: showHlaFileButton ?? false, key: PendingSwapsActionItem.DonorHlaFile, text: 'HLA FILE', disabled: false, group: ActionButtonGroup.Donors },
        { show: showCtReportButton ?? false, key: PendingSwapsActionItem.DonorCtReport, text: 'CT RPT', disabled: false, group: ActionButtonGroup.Donors },
        { show: showMedChartButton ?? false, key: PendingSwapsActionItem.DonorMedChart, text: 'MED CHART', disabled: false, group: ActionButtonGroup.Donors },
        { show: showAboVerifButton ?? false, key: PendingSwapsActionItem.DonorAboVerif, text: 'ABO VERIF', disabled: false, group: ActionButtonGroup.Donors },

        { show: showRequestSerologyKit ?? false, key: PendingSwapsActionItem.RequestSerologyKit, text: 'Request Kit', disabled, group: ActionButtonGroup.Serology },
        { show: showViewSerologyButton ?? false, key: PendingSwapsActionItem.ViewSerology, text: 'View', disabled: false, group: ActionButtonGroup.Serology },
        { show: showAddSerologyButton ?? false, key: PendingSwapsActionItem.UploadSerology, text: 'Upload', disabled, group: ActionButtonGroup.Serology },
        { show: showReuploadSerologyButton ?? false, key: PendingSwapsActionItem.ReuploadSerology, text: 'Re-Upload', disabled, group: ActionButtonGroup.Serology },

        { show: showViewFinalXmButton ?? false, key: PendingSwapsActionItem.ViewFinalXm, text: 'View', disabled: false, group: ActionButtonGroup.FinalXm },
        { show: showUploadFinalXmButton ?? false, key: PendingSwapsActionItem.UploadFinalXm, text: 'Upload', disabled, group: ActionButtonGroup.FinalXm },

        { show: showDonorCtStudy ?? false, key: PendingSwapsActionItem.DonorCtStudy, text: 'CT Study', disabled: false, group: ActionButtonGroup.Donors },
        { show: showCenterAboVerification ?? false, key: PendingSwapsActionItem.CenterAboVerification, text: 'Center ABO Verification', disabled: false, group: ActionButtonGroup.Donors },
        { show: showDonorAboMolecular ?? false, key: PendingSwapsActionItem.DonorAboMolecular, text: 'ABO Molecular', disabled: false, group: ActionButtonGroup.Donors },
        
        { 
            show: !props.isInternal, 
            key: PendingSwapsActionItem.FinancialInfo, 
            text: <FinancialAgreement recipientId={swap.recipient.id} donorId={swap.donor.id} centerId={centerId}>Financial Agreement</FinancialAgreement>, 
            disabled: false, 
            group: ActionButtonGroup.Swap, 
            displayOrder: 20 
        },
    ];

    const topButtons = buttons.filter(x => x.group == ActionButtonGroup.Top && x.show);
    const recipientButtons = buttons.filter(x => x.group == ActionButtonGroup.Recipients && x.show);
    const donorButtons = buttons.filter(x => x.group == ActionButtonGroup.Donors && x.show);
    const serologyButtons = buttons.filter(x => x.group == ActionButtonGroup.Serology && x.show);
    const finalXmButtons = buttons.filter(x => x.group == ActionButtonGroup.FinalXm && x.show);
    const swapButtons = buttons.filter(x => x.group == ActionButtonGroup.Swap && x.show);
    const otherButtons = buttons.filter(x => x.group == ActionButtonGroup.Other && x.show);

    const buttonGroups = [
        { name: 'Urgent', buttons: topButtons },
        { name: 'Recipient', buttons: recipientButtons },
        { name: 'Donor', buttons: donorButtons },
        { name: 'Serology', buttons: serologyButtons },
        { name: 'Final XM', buttons: finalXmButtons },
        { name: 'Swap', buttons: _.sortBy(swapButtons, x => x.displayOrder) },
        { name: 'Other', buttons: otherButtons }
    ];

    return (
        <>
            <ButtonGroupWrapper justified>
                <DropdownButton
                    bsStyle={'primary'}
                    bsSize='xs'
                    title={'Actions'}
                    key={props.index}
                    id={`ps-${props.index}`}
                    pullRight={props.pullRight}
                >
                    {(showEmergencyNotification ?? false) &&
                        <MenuItem className='alert alert-danger' style={{ padding: 2, marginBottom: 4, borderRadius: 0, border: '0 none' }} eventKey={PendingSwapsActionItem.EmergencyNotification} onSelect={handleMenuItemSelect}><span style={{ fontSize: 14, fontWeight: 600, display: 'block', textAlign: 'center' }}>Emergency Notification</span></MenuItem>
                    }

                    <MenuItem className='alert alert-info' style={{ padding: 2, marginBottom: 4, borderRadius: 0, border: '0 none' }} eventKey={PendingSwapsActionItem.Evaluate} onSelect={handleMenuItemSelect}><span style={{ fontSize: 14, fontWeight: 600, display: 'block', textAlign: 'center' }}>Evaluate</span></MenuItem>
                    <MenuItem divider style={{ marginTop: 0, marginBottom: 0 }} />

                    {buttonGroups.filter(x => x.buttons.length > 0).map((group, grpIndex) =>
                        <Fragment key={'btn-grp-' + grpIndex}>
                            <MenuItem header>{group.name}</MenuItem>
                            {group.buttons.map((btn, index) => <MenuItem key={'mi-' + index} style={{ marginLeft: 20 }} eventKey={btn.key} disabled={btn.disabled} onSelect={handleMenuItemSelect}>{btn.text}</MenuItem>)}
                        </Fragment>
                    )}
                    
                </DropdownButton>
            </ButtonGroupWrapper>
            
        </>
    );
}

export default Actions;